import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const publicLinks = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/trust-monitoring', label: 'Trust Monitoring' },
    { path: '/why-idnty', label: 'Why IDNTY-1' },
    { path: '/contact', label: 'Contact' },
  ];

  const authLinks = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/dashboard/monitoring', label: 'Monitoring' },
    { path: '/dashboard/identity', label: 'Identity' },
  ];

  // External link to playground
  const playgroundLink = { path: 'https://playground.idnty.one', label: 'Playground', external: true };

  const links = user ? authLinks : publicLinks;

  const handleSignOut = async () => {
    try {
      await signOut();
      // Close mobile menu if open
      setMobileMenuOpen(false);
      // Navigate to home page after sign out
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
      // Even if there's an error, navigate to home
      navigate('/');
    }
  };

  return (
    <nav style={{
      background: 'var(--white)',
      borderBottom: '1px solid var(--gray)',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
    }}>
      <div className="container" style={{ maxWidth: '1400px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '100px',
          padding: '0 1rem',
        }}>
          {/* Brand - Left */}
          <Link 
            to={user ? '/dashboard' : '/'} 
            style={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              flex: '0 0 auto',
            }}
          >
            <img 
              src="/fNew_logo_2.png" 
              alt="IDNTY Logo" 
              style={{ height: '80px', width: 'auto' }}
            />
          </Link>

          {/* Desktop Navigation - Center/Right */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '3rem',
            flex: 1,
          }} className="desktop-nav">
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${isActive(link.path) ? 'nav-link-active' : ''}`}
                style={{ fontSize: '1rem' }}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Playground Link */}
            <a
              href={playgroundLink.path}
              className="nav-link"
              style={{ fontSize: '1rem' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {playgroundLink.label}
            </a>
            
            {user ? (
              <button
                onClick={handleSignOut}
                className="btn btn-secondary"
                style={{ padding: '0.6rem 1.5rem', fontSize: '0.95rem' }}
              >
                Sign Out
              </button>
            ) : (
              <Link to="/login" className="btn btn-secondary" style={{ padding: '0.6rem 1.5rem', fontSize: '0.95rem', marginLeft: '1rem' }}>
                Log In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'none',
              padding: '0.5rem',
            }}
            className="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div style={{
            display: 'none',
            flexDirection: 'column',
            paddingBottom: '1rem',
            gap: '1rem',
          }} className="mobile-nav">
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${isActive(link.path) ? 'nav-link-active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                style={{ padding: '0.5rem 0' }}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Playground Link - Mobile */}
            <a
              href={playgroundLink.path}
              className="nav-link"
              style={{ padding: '0.5rem 0' }}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
            >
              {playgroundLink.label}
            </a>
            
            {user ? (
              <button
                onClick={() => {
                  handleSignOut();
                  setMobileMenuOpen(false);
                }}
                className="btn btn-secondary"
                style={{ width: '100%', marginTop: '0.5rem' }}
              >
                Sign Out
              </button>
            ) : (
              <Link 
                to="/login" 
                className="btn btn-secondary" 
                style={{ textAlign: 'center', marginTop: '0.5rem' }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Log In
              </Link>
            )}
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 968px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
          .mobile-nav {
            display: flex !important;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
